import React from 'react';
import { Target, Award, Users, TrendingUp, Shield, ShieldCheck, Clock, Lightbulb, Heart, Eye } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { missionVisionValues } from '../mock';

const About = () => {
  const iconMap = {
    shield: Shield,
    award: Award,
    'shield-check': ShieldCheck,
    clock: Clock,
    lightbulb: Lightbulb,
    heart: Heart
  };

  return (
    <section id="about" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main About Section */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            About QNC Co. Limited
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Jamaica's trusted construction partner
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start mb-20">
          {/* Left Content */}
          <div>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              Located at <span className="font-semibold text-gray-900">141B Constant Spring Rd, Kingston, Jamaica</span>, QNC Co. Limited is your trusted partner for comprehensive construction solutions.
            </p>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              We specialize in full-service construction, prefab steel buildings, generator solutions, and sourcing quality tools and equipment. Our team combines expertise, innovation, and dedication to bring your projects to life.
            </p>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              From architectural design to final installation, we handle every aspect of your project with precision and care.
            </p>

            <div className="flex items-center space-x-6 mb-8">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-1">10+</div>
                <div className="text-sm text-gray-600">Years Experience</div>
              </div>
              <div className="h-16 w-px bg-gray-300"></div>
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-1">50+</div>
                <div className="text-sm text-gray-600">Projects Completed</div>
              </div>
              <div className="h-16 w-px bg-gray-300"></div>
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-1">100%</div>
                <div className="text-sm text-gray-600">Client Satisfaction</div>
              </div>
            </div>

            <div className="bg-blue-50 rounded-xl p-6 border-l-4 border-blue-600">
              <p className="text-lg font-semibold text-blue-900 italic">
                "Building Strength. Shaping Jamaica."
              </p>
            </div>
          </div>

          {/* Right Content - Mission & Vision */}
          <div className="space-y-6">
            <Card className="border-2 border-blue-100">
              <CardHeader>
                <div className="flex items-center mb-2">
                  <Target className="w-6 h-6 text-blue-600 mr-3" />
                  <CardTitle className="text-2xl">Our Mission</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  {missionVisionValues.mission}
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-100">
              <CardHeader>
                <div className="flex items-center mb-2">
                  <Eye className="w-6 h-6 text-blue-600 mr-3" />
                  <CardTitle className="text-2xl">Our Vision</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  {missionVisionValues.vision}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Core Values Section */}
        <div className="mb-12">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {missionVisionValues.coreValues.map((value) => {
              const IconComponent = iconMap[value.icon];
              return (
                <Card
                  key={value.id}
                  className="group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-gray-100 hover:border-blue-200"
                >
                  <CardHeader>
                    <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-600 transition-colors duration-300">
                      <IconComponent className="w-7 h-7 text-blue-600 group-hover:text-white transition-colors duration-300" />
                    </div>
                    <CardTitle className="text-xl text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                      {value.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;